import React, { useState, useMemo } from 'react';
import {
  ShoppingBag, Music, Search, Plus, Edit2, Trash2, X,
  Clock, MapPin, User, Euro, ChevronDown, ChevronUp, Tag
} from 'lucide-react';
import {
  produits, professeurs, getProfesseur, getInstrumentsForCours,
  formatMontant, JOURS_SEMAINE
} from '../data/mockData';

const Produits = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategorie, setFilterCategorie] = useState('tous');
  const [expandedProduit, setExpandedProduit] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const coursProduits = produits.filter(p => p.categorie === 'cours');
  const instrumentProduits = produits.filter(p => p.categorie === 'instrument');
  const autreProduits = produits.filter(p => p.categorie === 'autre');

  const filtered = useMemo(() => {
    return produits.filter(p => {
      const matchSearch = !searchTerm ||
        p.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchCat = filterCategorie === 'tous' || p.categorie === filterCategorie;
      return matchSearch && matchCat;
    });
  }, [searchTerm, filterCategorie]);

  const filteredCours = filtered.filter(p => p.categorie === 'cours');
  const filteredInstruments = filtered.filter(p => p.categorie === 'instrument');
  const filteredAutre = filtered.filter(p => p.categorie === 'autre');

  const toggleExpand = (id) => {
    setExpandedProduit(expandedProduit === id ? null : id);
  };

  const renderCoursCard = (produit) => {
    const prof = getProfesseur(produit.professeurId);
    const instrumentsLies = getInstrumentsForCours(produit.id);
    const isExpanded = expandedProduit === produit.id;

    return (
      <div key={produit.id} className="cours-item">
        <div className="cours-item-header" onClick={() => toggleExpand(produit.id)}>
          <div className="cours-item-main">
            <h3>{produit.nom}</h3>
            <div className="cours-item-meta">
              {prof && (
                <span className="meta-item">
                  <User size={14} /> {prof.prenom} {prof.nom}
                </span>
              )}
              <span className="meta-item">
                <Clock size={14} /> {produit.duree} min
              </span>
              {produit.salle && (
                <span className="meta-item">
                  <MapPin size={14} /> {produit.salle}
                </span>
              )}
              <span className="meta-item">
                <Euro size={14} /> {formatMontant(produit.tarif)} / an
              </span>
            </div>
          </div>
          <div className="cours-item-right">
            <div className="cours-tags">
              {produit.frequence && produit.frequence.map(j => (
                <span key={j} className="cours-tag">{j}</span>
              ))}
            </div>
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
        </div>

        {isExpanded && (
          <div className="cours-item-details">
            <div className="details-grid">
              <div className="detail-block">
                <h4>Professeur</h4>
                {prof ? (
                  <div className="prof-info">
                    <div className="prof-avatar" style={{ background: prof.color }}>
                      {prof.initials}
                    </div>
                    <div>
                      <strong>{prof.prenom} {prof.nom}</strong>
                      <p className="meta-item">{prof.email}</p>
                    </div>
                  </div>
                ) : <p>Non assigné</p>}
              </div>
              <div className="detail-block">
                <h4>Planning</h4>
                <div className="creneaux-list">
                  {produit.frequence && produit.frequence.map(j => (
                    <div key={j} className="creneau-item">
                      <span className="creneau-jour">{j}</span>
                      <span>{produit.duree} min</span>
                      <span className="creneau-salle">{produit.salle}</span>
                    </div>
                  ))}
                </div>
              </div>
              {instrumentsLies.length > 0 && (
                <div className="detail-block detail-block-wide">
                  <h4>Instruments associés</h4>
                  <div className="cours-tags">
                    {instrumentsLies.map(inst => (
                      <span key={inst.id} className="cours-tag">
                        {inst.nom} — {formatMontant(inst.tarif)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="detail-actions">
              <button className="btn btn-secondary btn-sm">
                <Edit2 size={14} /> Modifier
              </button>
              <button className="btn btn-danger btn-sm">
                <Trash2 size={14} /> Supprimer
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderInstrumentCard = (produit) => {
    const coursLies = produit.coursAssocies
      ? produit.coursAssocies.map(cid => produits.find(p => p.id === cid)).filter(Boolean)
      : [];

    return (
      <div key={produit.id} className="produit-card" style={{ cursor: 'default' }}>
        <div className="produit-header">
          <span className="produit-ref">{produit.id}</span>
          <div className="produit-actions">
            <button className="btn-icon btn-icon-primary"><Edit2 size={14} /></button>
            <button className="btn-icon btn-icon-danger"><Trash2 size={14} /></button>
          </div>
        </div>
        <h3 className="produit-nom">{produit.nom}</h3>
        <p style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', marginBottom: '0.75rem' }}>
          {produit.description}
        </p>
        {coursLies.length > 0 && (
          <div style={{ marginBottom: '0.75rem' }}>
            <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)', display: 'block', marginBottom: '0.375rem' }}>
              Cours associés :
            </span>
            <div className="cours-tags">
              {coursLies.map(c => (
                <span key={c.id} className="cours-tag">{c.nom}</span>
              ))}
            </div>
          </div>
        )}
        <div className="produit-footer">
          <span style={{ fontWeight: 600, color: 'var(--primary)' }}>{formatMontant(produit.tarif)} / an</span>
        </div>
      </div>
    );
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1><ShoppingBag size={28} /> Produits</h1>
          <div className="page-meta">
            <span>{coursProduits.length} cours</span>
            <span>{instrumentProduits.length} instruments</span>
            <span>{autreProduits.length} autre(s)</span>
          </div>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <Plus size={16} /> Nouveau produit
          </button>
        </div>
      </div>

      <div className="filters-bar">
        <div className="search-input-group">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Rechercher un produit…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-tabs">
          {[
            { id: 'tous', label: 'Tous', count: produits.length },
            { id: 'cours', label: 'Cours', count: coursProduits.length },
            { id: 'instrument', label: 'Instruments', count: instrumentProduits.length },
            { id: 'autre', label: 'Autre', count: autreProduits.length },
          ].map(f => (
            <button
              key={f.id}
              className={`filter-tab ${filterCategorie === f.id ? 'active' : ''}`}
              onClick={() => setFilterCategorie(f.id)}
            >
              {f.label} ({f.count})
            </button>
          ))}
        </div>
      </div>

      {/* Cours de musique */}
      {filteredCours.length > 0 && (
        <div className="cours-section">
          <div className="section-header" style={{ borderLeftColor: 'var(--primary)' }}>
            <Music size={20} style={{ color: 'var(--primary)' }} />
            <h2>Cours de musique</h2>
            <span className="section-count">{filteredCours.length}</span>
          </div>
          <div className="cours-list">
            {filteredCours.map(renderCoursCard)}
          </div>
        </div>
      )}

      {/* Instruments */}
      {filteredInstruments.length > 0 && (
        <div className="cours-section">
          <div className="section-header" style={{ borderLeftColor: 'var(--accent)' }}>
            <Tag size={20} style={{ color: 'var(--accent)' }} />
            <h2>Instruments</h2>
            <span className="section-count">{filteredInstruments.length}</span>
          </div>
          <div className="produits-grid">
            {filteredInstruments.map(renderInstrumentCard)}
          </div>
        </div>
      )}

      {/* Autre */}
      {filteredAutre.length > 0 && (
        <div className="cours-section">
          <div className="section-header" style={{ borderLeftColor: 'var(--success)' }}>
            <ShoppingBag size={20} style={{ color: 'var(--success)' }} />
            <h2>Autre</h2>
            <span className="section-count">{filteredAutre.length}</span>
          </div>
          <div className="produits-grid">
            {filteredAutre.map(p => (
              <div key={p.id} className="produit-card" style={{ cursor: 'default' }}>
                <div className="produit-header">
                  <span className="produit-ref">{p.id}</span>
                  <div className="produit-actions">
                    <button className="btn-icon btn-icon-primary"><Edit2 size={14} /></button>
                  </div>
                </div>
                <h3 className="produit-nom">{p.nom}</h3>
                <p style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', marginBottom: '0.75rem' }}>
                  {p.description}
                </p>
                {p.obligatoire && (
                  <span className="badge badge-warning">Obligatoire</span>
                )}
                <div className="produit-footer">
                  <span style={{ fontWeight: 600, color: 'var(--primary)' }}>{formatMontant(p.tarif)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Nouveau produit</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Catégorie</label>
                <select className="filter-select" style={{ width: '100%' }}>
                  <option value="cours">Cours de musique</option>
                  <option value="instrument">Instrument</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              <div className="form-group">
                <label>Nom du produit</label>
                <input type="text" placeholder="Ex: Piano - Cours individuel" />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea placeholder="Description du produit…" />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Tarif annuel (€)</label>
                  <input type="number" placeholder="450" />
                </div>
                <div className="form-group">
                  <label>Durée (min)</label>
                  <input type="number" placeholder="30" />
                </div>
              </div>
              <div className="form-group">
                <label>Fréquence (jours)</label>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {JOURS_SEMAINE.map(j => (
                    <label key={j} className="checkbox-label" style={{ padding: '0.375rem 0.75rem', background: 'var(--gray-100)', borderRadius: 'var(--radius-sm)', fontSize: '0.8125rem' }}>
                      <input type="checkbox" />
                      {j}
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Professeur</label>
                  <select>
                    <option value="">— Sélectionner —</option>
                    {professeurs.map(p => (
                      <option key={p.id} value={p.id}>{p.prenom} {p.nom}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Salle</label>
                  <input type="text" placeholder="Salle 1" />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={() => setShowModal(false)}>Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Produits;
